import { useEffect, useState } from 'react';
import type { MetaFields } from '@dutchiesdk/ecommerce-extensions-sdk';
import { useRemoteCommerceComponents } from './remote-theme';
import { useCommerceBridge } from './bridge/bridge-context';

/**
 * Hook to get meta fields from the remote theme's getStoreFrontMetaFields function
 * Returns the MetaFields object that can be used to render meta tags
 */
type ResolvedMetaFields = {
  // The route and theme function the value was resolved for. A value is only exposed while both
  // still match the current render, so a navigation (or theme swap) never surfaces the previous
  // route's canonical/og:image/JSON-LD while the new request is pending.
  pathname: string;
  getMetaFieldsFn: unknown;
  themeIdentity: string | undefined;
  fields: MetaFields | null;
};

// `themeIdentity` (manifestUrl/scope/modulePath) is the active store's theme target. During the one
// render where it already names a NEW theme but `remoteThemeModule` still holds the OLD theme's
// function, the function reference and pathname both still match the previous resolution, so the
// identity is what keeps the old theme's fields from being exposed.
export const useMetaFields = (skipExtraction = false, themeIdentity?: string): MetaFields | null => {
  const [resolved, setResolved] = useState<ResolvedMetaFields | null>(null);
  const { remoteThemeModule } = useRemoteCommerceComponents();
  const dataBridgeData = useCommerceBridge();
  const isBridgeReady = dataBridgeData != null;

  // Extract stable values to avoid infinite loops
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const getMetaFieldsFn = remoteThemeModule.default?.getStoreFrontMetaFields;

  useEffect(() => {
    let isCurrentRequest = true;
    const setMetaFields = (fields: MetaFields | null): void =>
      setResolved({ pathname, getMetaFieldsFn, themeIdentity, fields });

    const loadMetaFields = async (): Promise<void> => {
      if (!getMetaFieldsFn || dataBridgeData == null || skipExtraction) {
        setMetaFields(null);
        return;
      }

      try {
        const fields = await getMetaFieldsFn(dataBridgeData);
        if (isCurrentRequest) {
          setMetaFields(fields);
        }
      } catch {
        if (isCurrentRequest) {
          setMetaFields(null);
        }
      }
    };

    void loadMetaFields();
    // The shared bridge is stable, so only readiness (not cart/user value changes) participates in
    // extraction. This also clears metadata if the provider graph becomes unavailable.
    return () => {
      isCurrentRequest = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, getMetaFieldsFn, themeIdentity, isBridgeReady, skipExtraction]);

  return resolved &&
    resolved.pathname === pathname &&
    resolved.getMetaFieldsFn === getMetaFieldsFn &&
    resolved.themeIdentity === themeIdentity
    ? resolved.fields
    : null;
};
